
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { ArrowLeft, CheckCircle } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

const About = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-52 pb-16 md:pt-64 md:pb-24 bg-gradient-to-b from-apple-blue/5 to-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block px-4 py-1.5 mb-6 rounded-full bg-apple-blue/10 text-apple-blue font-medium text-sm">
              Our Story
            </div>
            <h1 className="text-4xl md:text-6xl font-semibold text-apple-text mb-6 leading-tight">
              Transforming India's Hiring Landscape
            </h1>
            <p className="text-lg md:text-xl text-apple-text-secondary mb-6">
              An innovative approach to solving India's hiring challenges through artificial intelligence and technology.
            </p>
            <Link to="/" className="inline-flex items-center text-apple-blue font-medium">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Home
            </Link>
          </div>
        </div>
      </section>

      {/* Founder Story Section */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
              <div className="space-y-6">
                <h2 className="text-3xl md:text-4xl font-semibold text-apple-text leading-tight">
                  The Visionary Behind True Talent Apply
                </h2>
                <div className="space-y-4 text-apple-text-secondary">
                  <p>
                    In 2018, after witnessing the inefficiencies in traditional hiring processes across India's rapidly growing business landscape, Deepak Pokharna envisioned a future where technology could bridge the gap between talent and opportunity.
                  </p>
                  <p>
                    With over 15 years of experience in talent acquisition and human resources, Deepak understood the unique challenges that both employers and job seekers faced in India - from the overwhelming volume of applications to the difficulty in identifying the right skill match.
                  </p>
                  <p>
                    "The disconnect was clear," Deepak recalls. "Companies were struggling to find qualified candidates despite thousands of applications, while deserving candidates couldn't showcase their true potential through traditional resumes."
                  </p>
                  <p>
                    This realization sparked a journey to create a platform that would leverage artificial intelligence to transform how organizations identify, assess, and engage with talent. The goal was simple yet ambitious: to make hiring more efficient, transparent, and human-centered through technology.
                  </p>
                  <p>
                    Today, our AI-powered platform serves thousands of organizations across India, from startups to multinational corporations, helping them build stronger teams while providing candidates with fair opportunities to demonstrate their capabilities.
                  </p>
                </div>
              </div>
              <div className="relative">
                <div className="absolute -top-6 -left-6 w-24 h-24 bg-apple-blue/10 rounded-full"></div>
                <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-apple-blue/5 rounded-full"></div>
                <div className="relative z-10 bg-white rounded-2xl shadow-xl overflow-hidden">
                  <img 
                    src="/public/lovable-uploads/082c3595-f436-417c-ab0e-e8ee85365ab4.png"
                    alt="Deepak Pokharna, Founder" 
                    className="w-full aspect-auto object-cover object-center"
                  />
                  <div className="p-6 bg-white">
                    <h3 className="text-xl font-semibold text-apple-text mb-1">Deepak Pokharna</h3>
                    <p className="text-apple-blue font-medium mb-3">Founder & CEO</p>
                    <p className="text-apple-text-secondary text-sm">
                      "Our mission is to democratize opportunities for India's talent and empower organizations to build extraordinary teams through technology."
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission and Values */}
      <section className="py-16 md:py-24 bg-apple-gray">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-semibold text-apple-text mb-4">
              Our Mission and Values
            </h2>
            <p className="text-lg text-apple-text-secondary">
              Guided by a commitment to innovation, fairness, and excellence in everything we do.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="bg-white border-0 shadow-sm hover:shadow-md transition-all duration-300">
              <CardContent className="pt-6">
                <div className="w-12 h-12 rounded-full bg-apple-blue/10 flex items-center justify-center mb-5 text-apple-blue">
                  <CheckCircle className="h-6 w-6" />
                </div>
                <h3 className="text-xl font-semibold text-apple-text mb-3">Innovation</h3>
                <p className="text-apple-text-secondary">
                  We constantly push the boundaries of what's possible in recruitment technology, staying ahead of industry trends to provide cutting-edge solutions.
                </p>
              </CardContent>
            </Card>
            <Card className="bg-white border-0 shadow-sm hover:shadow-md transition-all duration-300">
              <CardContent className="pt-6">
                <div className="w-12 h-12 rounded-full bg-apple-blue/10 flex items-center justify-center mb-5 text-apple-blue">
                  <CheckCircle className="h-6 w-6" />
                </div>
                <h3 className="text-xl font-semibold text-apple-text mb-3">Fairness</h3>
                <p className="text-apple-text-secondary">
                  We're committed to creating a level playing field where candidates are evaluated based on their true potential, not just their resume formatting skills.
                </p>
              </CardContent>
            </Card>
            <Card className="bg-white border-0 shadow-sm hover:shadow-md transition-all duration-300">
              <CardContent className="pt-6">
                <div className="w-12 h-12 rounded-full bg-apple-blue/10 flex items-center justify-center mb-5 text-apple-blue">
                  <CheckCircle className="h-6 w-6" />
                </div>
                <h3 className="text-xl font-semibold text-apple-text mb-3">Excellence</h3>
                <p className="text-apple-text-secondary">
                  We pursue excellence in every aspect of our platform, from user experience to the accuracy of our AI algorithms.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-gradient-to-r from-apple-blue/10 to-apple-blue/5">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-semibold text-apple-text mb-6">
              Join Us in Transforming Hiring
            </h2>
            <p className="text-lg text-apple-text-secondary mb-8">
              Whether you're looking for your next career opportunity or seeking to build a stronger team for your organization, we're here to help.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button asChild className="bg-apple-blue hover:bg-apple-blue/90 text-white font-medium px-6 py-2.5 rounded-lg">
                <Link to="/signup">Apply for a Position</Link>
              </Button>
              <Button asChild variant="outline" className="border-apple-blue text-apple-blue hover:bg-apple-blue/5 font-medium px-6 py-2.5 rounded-lg">
                <Link to="/dashboard">Employer Dashboard</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default About;
